import { useEffect, useState } from "react";

import { Field } from "react-final-form";

import { getTimezones, Timezone } from "@/api/endpoints/timezones";
import ClockIcon from "@/assets/icons/clock.svg";
import InformationCircle from "@/assets/icons/information-circle.svg";
import { CURRENCY, LANGUAGE, LOCALE, USER_ACCESS } from "@/constants";
import { sequenceValidators, validateEmail, validateRequired } from "@/utils";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import { LanguageDisplay } from "@components/DataDisplay";
import RoleDisplay from "@components/DataDisplay/RoleDisplay";
import {
  FF_InputField,
  FF_SelectField,
  FF_SwitchToggle,
} from "@components/Field";
import Adornment from "@components/Field/Adornment";
import { i18n } from "@lingui/core";

export const USER_SETTINGS_FIELDS = {
  NAME: "name",
  EMAIL: "email",
  LANGUAGE: "language",
  ACCESS: "user_type",
  CURRENCY: "currency",
  TIME_ZONE: "time_zone",
  REPORT_TIME: "preferred_mail_delivery_time",
  WEEKEND_EMAILS: "send_mail_on_weekends",
};

const USER_SETTINGS_DEFAULT = Object.values(USER_SETTINGS_FIELDS).filter(
  (f) => f !== USER_SETTINGS_FIELDS.ACCESS
);

const TimezoneField = () => {
  const [timezones, setTimezones] = useState<Timezone[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    getTimezones()
      .then(({ status, data }) => {
        if (status === 200 && data) {
          setReady(true);
          setTimezones(data);
        }
      })
      .catch(() => {
        // failed to get timezones
      });
  }, []);
  return (
    <Field
      block
      disabled={!ready}
      component={FF_SelectField}
      label={L.USER_SETTINGS_GENERIC_TIMEZONE_LABEL}
      name={USER_SETTINGS_FIELDS.TIME_ZONE}
      options={timezones.map((tz) => ({ value: tz, label: tz }))}
      placeholder={L.USER_SETTINGS_GENERIC_TIMEZONE_PLACEHOLDER}
    />
  );
};

/**
 * UserSettingFields
 * Renders a set of fields for user settings, made to be highly reusable.
 * Assuming parent ( at any level ) is a FinalForm Form.
 *
 * NOTE: If this setup is too clunky, we can always just split the fields into their own components.
 *
 * @prop fields Accepts an array of user fields to render, defaults to all fields except for access..
 * @prop readOnlyFields By default a user cannot change name or email. passing false will make them changeable.
 */
const UserSettingFields = ({
  readOnlyFields = true,
  fields = USER_SETTINGS_DEFAULT,
}) => (
  <>
    {fields.includes(USER_SETTINGS_FIELDS.NAME) && (
      <Field
        block
        component={FF_InputField}
        label={L.USER_SETTINGS_GENERIC_NAME_LABEL}
        name={USER_SETTINGS_FIELDS.NAME}
        readOnly={readOnlyFields}
      />
    )}

    {fields.includes(USER_SETTINGS_FIELDS.EMAIL) && (
      <Field
        block
        component={FF_InputField}
        autoComplete="off"
        label={L.USER_SETTINGS_GENERIC_EMAIL_LABEL}
        placeholder={L.USER_SETTINGS_FIELDS_EMAIL_PLACEHOLDER}
        name={USER_SETTINGS_FIELDS.EMAIL}
        readOnly={readOnlyFields}
        validate={
          readOnlyFields
            ? undefined
            : sequenceValidators(
                validateRequired(i18n._(L.REQUIRED_FIELD_VALUE)),
                validateEmail(i18n._(L.INVALID_EMAIL))
              )
        }
        type="email"
      />
    )}

    {fields.includes(USER_SETTINGS_FIELDS.LANGUAGE) && (
      <Field
        block
        component={FF_SelectField}
        label={L.USER_SETTINGS_GENERIC_LANGUAGE_LABEL}
        name={USER_SETTINGS_FIELDS.LANGUAGE}
      >
        <LanguageDisplay
          id={LANGUAGE.SWEDISH}
          render={(v) => <option value={LOCALE.SWEDISH}>{v}</option>}
        />
        <LanguageDisplay
          id={LANGUAGE.ENGLISH}
          render={(v) => <option value={LOCALE.ENGLISH}>{v}</option>}
        />
      </Field>
    )}

    {fields.includes(USER_SETTINGS_FIELDS.ACCESS) && (
      <Field
        block
        component={FF_SelectField}
        helperText={
          <>
            {L.ORGANIZATION_INVITE_USER_ACCESS_LEVEL_HELPER}{" "}
            <a
              href={L.ORGANIZATION_INVITE_USER_ACCESS_LEVEL_HELPER_LINK}
              rel="noreferrer"
              target="_blank"
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              {" "}
              <InformationCircle width="1rem" />
            </a>
          </>
        }
        label={<span>{L.ORGANIZATION_INVITE_USER_ACCESS_LEVEL}</span>}
        name={USER_SETTINGS_FIELDS.ACCESS}
      >
        <AccessControl level={ACCESS_LEVELS.ADMIN}>
          <option value={USER_ACCESS.ADMIN}>
            <RoleDisplay id={USER_ACCESS.ADMIN} />
          </option>
        </AccessControl>
        <AccessControl level={ACCESS_LEVELS.USER}>
          <option value={USER_ACCESS.USER}>
            <RoleDisplay id={USER_ACCESS.USER} />
          </option>
        </AccessControl>
        <option value={USER_ACCESS.READONLY}>
          <RoleDisplay id={USER_ACCESS.READONLY} />
        </option>
      </Field>
    )}

    {fields.includes(USER_SETTINGS_FIELDS.CURRENCY) && (
      <Field
        block
        component={FF_SelectField}
        label={L.USER_SETTINGS_GENERIC_CURRENCY_LABEL}
        name={USER_SETTINGS_FIELDS.CURRENCY}
        placeholder={L.USER_SETTINGS_GENERIC_CURRENCY_PLACEHOLDER}
      >
        <option value={CURRENCY.SEK}>
          {L.ACCOUNT_VIEW_ACCOUNT_CURRENCIES_SEK}
        </option>
        <option value={CURRENCY.USD}>
          {L.ACCOUNT_VIEW_ACCOUNT_CURRENCIES_DOLLAR}
        </option>
        <option value={CURRENCY.EUR}>
          {L.ACCOUNT_VIEW_ACCOUNT_CURRENCIES_EUR}
        </option>
        <option value={CURRENCY.NOK}>
          {L.ACCOUNT_VIEW_ACCOUNT_CURRENCIES_NOK}
        </option>
      </Field>
    )}

    {fields.includes(USER_SETTINGS_FIELDS.TIME_ZONE) && <TimezoneField />}

    {fields.includes(USER_SETTINGS_FIELDS.WEEKEND_EMAILS) && (
      <Field
        block
        component={FF_SwitchToggle}
        label={L.USER_SETTINGS_GENERIC_REPORT_WEEKENDS}
        name={USER_SETTINGS_FIELDS.WEEKEND_EMAILS}
        data-testid={USER_SETTINGS_FIELDS.WEEKEND_EMAILS}
        type="checkbox"
      />
    )}
    {fields.includes(USER_SETTINGS_FIELDS.REPORT_TIME) && (
      <Field
        block
        component={FF_InputField}
        data-testid={USER_SETTINGS_FIELDS.REPORT_TIME}
        endAdornment={
          <Adornment>
            <ClockIcon />
          </Adornment>
        }
        helperText={L.USER_SETTINGS_GENERIC_REPORT_TIME_DISCLAIMER}
        label={L.USER_SETTINGS_GENERIC_REPORT_TIME}
        name={USER_SETTINGS_FIELDS.REPORT_TIME}
        type="time"
      />
    )}
  </>
);

export default UserSettingFields;
